import React, { useContext, useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import CircularProgress from "@material-ui/core/CircularProgress";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, { createFilterOptions, } from "@material-ui/lab/Autocomplete";

import { Grid } from "@material-ui/core";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import SingleSelect from "../InputStyled/singleSelect";
import SendTemplateModal from "../SendTemplateModal";

const filter = createFilterOptions({
	trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact = {}, completeConnectionsAndQueues = [] }) => {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [selectedConnection, setSelectedConnection] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [tags, setTags] = useState([]);
	const [selectedTemplateData, setSelectedTemplateData] = useState(null);
	const [waMessagingWindow, setWaMessagingWindow] = useState(null);
	const [waMessagingWindowLoading, setWaMessagingWindowLoading] = useState(false);
	const { user } = useContext(AuthContext);

	const isConnectionAllowed = (connection) =>
		connection &&
		(connection.moduleId == 1 || connection.moduleId == 4 || connection.moduleId == 3) &&
		String(connection.status).toLowerCase() === "connected";

	const getSelectedConnection = () => {
		if (!selectedConnection || !Array.isArray(user?.completeConnectionsAndQueues)) return null;
		return user.completeConnectionsAndQueues.find(
			(ccq) => ccq?.connection?.id == selectedConnection
		)?.connection;
	};

	const isWaOfficialConnection = getSelectedConnection()?.moduleId == 3;
	/** Template obrigatório só quando a janela de 24h está fechada (WA Oficial). */
	const waTemplateRequired =
		isWaOfficialConnection &&
		selectedQueue &&
		selectedContact &&
		waMessagingWindow &&
		waMessagingWindow.open === false;
	const showWaTemplateSection = waTemplateRequired;

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/tags");
				if (!data || !data.tags) data.tags = [];
				if (data.tags) data.tags.unshift({ id: 0, adminId: user.adminId, color: '#000000', name: 'sem etiqueta', updatedBy: user.id, status: 1, });
				setTags(data.tags)
			} catch (error) {
				console.error(error)
			}
		})();
	}, []);

	useEffect(() => {
		if (!modalOpen) return;

		const response = [];
		if (Array.isArray(user?.completeConnectionsAndQueues)) {
			user.completeConnectionsAndQueues.map(ccq => {
				if (ccq?.connection && isConnectionAllowed(ccq.connection)) {
					if (ccq?.queues?.length == 1 && ccq.queues[0]?.name == "FILA_CHATBOT") return;
					response.push({ key: ccq.connection.id, value: ccq.connection.name, disabled: false });
				}
			})
		}

		if (response.length == 1) setSelectedConnection(response[0].key);

	}, [modalOpen]);

	useEffect(() => {
		if (initialContact?.id !== undefined) {
			setOptions([initialContact]);
			setSelectedContact(initialContact);
		}
	}, [initialContact]);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		let isMounted = true;
        const abortController = new AbortController();

		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { 
							searchParam, 
							searchConnection: selectedConnection
						},
						signal: abortController.signal,
					});
					if (!isMounted) return;
					setOptions(data.contacts);
					setLoading(false);
				} catch (err) {
					if (!isMounted || err.name == 'AbortError') return;
					console.error({err});
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();

			return () => {
				isMounted = false;
				abortController.abort();
			};
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam]);

	useEffect(() => {
		if (!selectedConnection || selectedConnection == "") {
			setSelectedQueue(null);
			setSelectedTemplateData(null);
			setWaMessagingWindow(null);
			return;
		}

		// verificar se houver só uma fila, deixa-lá selecionada
		const conn = completeConnectionsAndQueues.find(cq => cq?.connection?.id == selectedConnection);
		if (conn?.queues && Array.isArray(conn.queues)) {
			const filteredQueues = conn.queues.filter(q => q.name != "FILA_CHATBOT");
			if (filteredQueues.length == 1) {
				setSelectedQueue(filteredQueues[0].id);
				return;
			}
		}

		setSelectedQueue(null);
		setSelectedTemplateData(null);
		setWaMessagingWindow(null);

	}, [selectedConnection]);

	/** Consulta janela 24h só para WA Oficial (não afeta baileys/whatsmeow). */
	useEffect(() => {
		const connection = Array.isArray(user?.completeConnectionsAndQueues)
			? user.completeConnectionsAndQueues.find(
					(ccq) => ccq?.connection?.id == selectedConnection
			  )?.connection
			: null;
		const isOfficial = connection?.moduleId == 3;

		if (!modalOpen || !isOfficial || !selectedContact?.id || !selectedConnection) {
			setWaMessagingWindow(null);
			setWaMessagingWindowLoading(false);
			return;
		}

		let cancelled = false;
		setWaMessagingWindowLoading(true);
		setWaMessagingWindow(null);
		setSelectedTemplateData(null);

		api
			.get("/messages/wabusiness/messaging-window-by-contact", {
				params: {
					contactId: selectedContact.id,
					whatsappId: selectedConnection,
				},
			})
			.then(({ data }) => {
				if (cancelled) return;
				setWaMessagingWindow(data || { open: false, reason: "no_client_message" });
			})
			.catch((err) => {
				if (cancelled) return;
				console.error({ err });
				// Em falha, exige template (comportamento seguro para Meta).
				setWaMessagingWindow({ open: false, reason: "no_client_message" });
			})
			.finally(() => {
				if (!cancelled) setWaMessagingWindowLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [modalOpen, selectedConnection, selectedContact?.id]);

	const handleClose = (ticket = undefined) => {
		onClose(ticket);
		setSearchParam("");
		setOptions([]);
		setSelectedContact(null);
		setSelectedConnection(null);
		setSelectedQueue(null);
		setNewContact({});
		setSelectedTemplateData(null);
		setWaMessagingWindow(null);
		setWaMessagingWindowLoading(false);
	};

	const handleSaveTicket = async (contactId) => {
		if (loading) return;

		if (!contactId) return;
	
		if (!selectedQueue || selectedQueue == "") {
			toast.error("Selecione uma fila");
			return;
		}

		if (isWaOfficialConnection && waMessagingWindowLoading) {
			return;
		}

		if (waTemplateRequired && !selectedTemplateData?.template) {
			toast.error(i18n.t("newTicketModal.templateRequiredOutsideWindow"));
			return;
		}
	
		setLoading(true);
		let createdTicket = null;
		try {
			const { data: ticket } = await api.post("/tickets", {
				contactId: contactId,
				queueId: selectedQueue,
				userId: user.id,
				status: "open",
				whatsappId: selectedConnection
			});
			createdTicket = ticket;

			if (isWaOfficialConnection && selectedTemplateData?.template) {
				try {
					const { data } = await api.post(`/messages/${ticket.id}/template`, {
						templateName: selectedTemplateData.template.name,
						language: selectedTemplateData.template.language,
						variables: selectedTemplateData.variables || {},
						components: selectedTemplateData.template.components,
					});
					toast.success(
						data?.message ? i18n.t(data.message) : i18n.t("sendTemplateModal.sentToMeta")
					);
				} catch (templateErr) {
					console.error({ templateErr });
					toastError(templateErr);
					toast.warn(i18n.t("newTicketModal.templateSendFailedTicketCreated"));
					handleClose(createdTicket);
					return;
				}
			}

			handleClose(createdTicket);
		} catch (err) {
			console.error({ err });
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const isValidPhone = (str) => {
		const cleanRegex = /^(\+?55\s?)?(\(?\d{2}\)?\s?)?(9\d{4}|[2-9]\d{3})-?\d{4}$/;
	  
		return cleanRegex.test(str.replace(/\s+/g, ''));
	  }

	const handleSelectOption = (e, newValue) => {
		if (newValue?.number || newValue?.moduleId == 2 || newValue?.moduleId == 3) {
			setSelectedContact(newValue);
		} else if (newValue?.name) {
			let props = {};
			const isValid = isValidPhone(newValue.name);
			// se o texto digitado for somente números, setar como número se não como nome
			if (isValid == true) {
				props.number = String(newValue.name).replace(/\D/g, '');
				props.name = newValue.name;
			}
			else { props.name = newValue.name; }
			if (selectedConnection) {
				// passar whatsappId — ContactModal usa connection.id no select
				props.connection = selectedConnection;
			}
			setNewContact(props);
			setContactModalOpen(true);
		}
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const handleAddNewContactTicket = (contact) => {
		setContactModalOpen(false);
		if (!contact?.id) return;

		// Contato novo: seleciona no modal para o fluxo Oficial poder exigir template.
		setOptions([contact]);
		setSelectedContact(contact);

		// Baileys / Whatsmeow: mantém comportamento anterior (cria ticket na hora).
		if (!isWaOfficialConnection) {
			handleSaveTicket(contact.id);
		}
	};

	const createAddContactOption = (filterOptions, params) => {
		const safeOptions = (filterOptions || []).filter((opt) => opt != null);
		const filtered = filter(safeOptions, {
			...params,
			getOptionLabel: (option) => renderOptionLabel(option) || "",
		});

		if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
			filtered.push({
				name: `${params.inputValue}`,
			});
		}

		return filtered;
	};

	const renderOption = option => {
		if (!option) return "";
		if (option.number && (option.moduleId == 1 || option.moduleId == 4 || option.moduleId == 3 || option.moduleId == null)) {
			let str = `${option.isGroup ? i18n.t('groupPrefix') : ''}${option.name} - ${option.number}`;
			if (option.whatsappName) str += ` (${option.whatsappName})`;
			return str;
		} else if (option.moduleId == 2) {
			let str = `${option.name} - `;
			if (option.whatsappName) str += ` (${option.whatsappName})`;
			return str;
		} else if (!option.number && (option.moduleId == 1 || option.moduleId == 4 || option.moduleId == 3 || option.moduleId == null)) {
			return `${i18n.t("newTicketModal.add")} ${option.isGroup ? i18n.t('groupPrefix') : ''}${option.name}`;
		}
		return option.name || option.number || "";
	};

	const renderOptionLabel = option => {
		if (!option) return "";
		if (option.number && (option.moduleId == 1 || option.moduleId == 4 || option.moduleId == 3 || option.moduleId == null)) {
			return `${option.isGroup ? i18n.t('groupPrefix') : ''}${option.name} - ${option.number}`;
		} else if (option.moduleId == 2) {
			return `${option.name}`;
		} else if (!option.number && (option.moduleId == 1 || option.moduleId == 4 || option.moduleId == 3 || option.moduleId == null)) {
			return `${option.isGroup ? i18n.t('groupPrefix') : ''}${option.name}`;
		}
		return option.name || option.number || "";
	};

	const renderConnectionOptions = () => {
		const response = [];
		if (Array.isArray(user?.completeConnectionsAndQueues)) {
			user.completeConnectionsAndQueues.map(ccq => {
				if (ccq?.connection && isConnectionAllowed(ccq.connection)) {
					if (ccq?.queues?.length == 1 && ccq.queues[0]?.name == "FILA_CHATBOT") return;
					response.push({ key: ccq.connection.id, value: ccq.connection.name, disabled: false });
				}
			})
		}
		return response;
	}

	const renderQueueOptions = () => {
		const response = [];
		if (Array.isArray(user?.completeConnectionsAndQueues) && selectedConnection) {
			const connection = user.completeConnectionsAndQueues.find(cq => cq?.connection?.id == selectedConnection);
			if (connection && Array.isArray(connection?.queues)) {
				connection.queues.map((q) => {
					if (q?.name == "FILA_CHATBOT") return;
					response.push({
						key: q.id,
						value: q.name,
						disabled: false
					});
				});
			}
		}
		return response;
	}

	const okDisabled =
		!selectedContact ||
		(isWaOfficialConnection && waMessagingWindowLoading) ||
		(waTemplateRequired && !selectedTemplateData?.template);

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={handleAddNewContactTicket}
				byContactPage={false}
				tags={tags.filter((tag) => tag.id >= 1)}
			></ContactModal>
			<Dialog
				open={modalOpen}
				onClose={handleClose}
				maxWidth={showWaTemplateSection ? "lg" : "sm"}
				fullWidth={true}
			>
				<DialogTitle id="form-dialog-title">
					{i18n.t("newTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<Grid container spacing={2}>
						<Grid xs={6} item>
							<SingleSelect
								title={
									<Typography>
										Conexão
										<Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
									</Typography>
								}
								displayText={"selecionar conexão"}
								name="newTicketConnection"
								key="newTicketConnection"
								options={renderConnectionOptions()}
								onSelected={(opt) => setSelectedConnection(opt)}
								selected={selectedConnection}
								closeOnSelect={true}
							/>
						</Grid>
						<Grid xs={6} item>
						
							<SingleSelect
								title={
									<Typography>
										Fila
										<Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
									</Typography>
								}
								displayText={"selecionar fila"}
								name="newTicketQueue"
								key="newTicketQueue"
								options={renderQueueOptions()}
								onSelected={(opt) => setSelectedQueue(opt)}
								selected={selectedQueue}
								closeOnSelect={true}
								disabled={!selectedConnection || selectedConnection == "" ? true : false}
							/>
						</Grid>
					</Grid>
					<Grid container spacing={2} hidden={initialContact?.id !== undefined}>
						<Grid xs={12} item>
							<Typography>
								Contato
								<Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
							</Typography>
							<Autocomplete
								fullWidth
								options={options}
								loading={loading}
								clearOnBlur
								disabled={!selectedQueue || selectedQueue == ""}
								autoHighlight
								placeholer={i18n.t("newTicketModal.fieldLabel")}
								freeSolo
								clearOnEscape
								getOptionLabel={renderOptionLabel}
								renderOption={!loading ? renderOption : undefined}
								filterOptions={createAddContactOption}
								onChange={(e, newValue) => handleSelectOption(e, newValue)}
								renderInput={params => (
									<>
										<TextField
											{...params}
											paddingTop='10px'
											autoFocus
											placeholder="Digite para pesquisar o contato"
											fullWidth
											margin="normal"
											variant="outlined"
											onChange={e => setSearchParam(e.target.value)}
											InputLabelProps={{
												shrink: true,
											}}
											InputProps={{
												...params.InputProps,
												style: {
													padding: '1px 9px 5px 10px',
													fontSize: '15px',
												},
												endAdornment: (
													<React.Fragment>
														{loading ? (
															<CircularProgress color="inherit" size={20} />
														) : null}
														{params.InputProps.endAdornment}
													</React.Fragment>
												),
											}}
										/>
									</>
								)}
							/>
						</Grid>
					</Grid>
					{isWaOfficialConnection && selectedContact && waMessagingWindowLoading && (
						<Grid container spacing={2} style={{ marginTop: 8 }}>
							<Grid item xs={12} style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<CircularProgress size={18} />
								<Typography color="textSecondary">
									{i18n.t("newTicketModal.checkingMessagingWindow")}
								</Typography>
							</Grid>
						</Grid>
					)}
					{isWaOfficialConnection &&
						selectedContact &&
						!waMessagingWindowLoading &&
						waMessagingWindow?.open === true && (
						<Grid container spacing={2} style={{ marginTop: 8 }}>
							<Grid item xs={12}>
								<Typography color="textSecondary">
									{i18n.t("newTicketModal.messagingWindowOpen")}
								</Typography>
							</Grid>
						</Grid>
					)}
					{showWaTemplateSection && (
						<Grid container spacing={2} style={{ marginTop: 8 }}>
							<Grid item xs={12}>
								<Typography style={{ marginBottom: 8 }}>
									{i18n.t("sendTemplateModal.title")}
								</Typography>
								<Typography color="textSecondary" style={{ marginBottom: 8 }}>
									{i18n.t("newTicketModal.templateRequiredOutsideWindow")}
								</Typography>
								<SendTemplateModal
									open
									embedded
									whatsappId={selectedConnection}
									queueId={selectedQueue}
									onTemplateChange={setSelectedTemplateData}
								/>
							</Grid>
						</Grid>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
					>
						{i18n.t("newTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="button"
						disabled={okDisabled}
						onClick={() => handleSaveTicket(selectedContact.id)}
						color="primary"
						loading={loading}
					>
						{i18n.t("newTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewTicketModal;
